import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * DEV-019 UAT3 · Canonical Sales lifecycle stage control on the
 * Operations Project Detail. Distinct from operational ``current_stage``.
 */
const FALLBACK = {
    enquiry: "Enquiry",
    contacted_qualified: "Contacted / Qualified",
    sales_appointment: "Sales Survey / Appointment",
    quote_proposal: "Quote / Proposal",
    decision: "Decision",
};

export default function SalesStageSection({ project, onProjectUpdated }) {
    const { user } = useAuth();
    const canEdit = useMemo(() => {
        if (!user) return false;
        const role = (user.role || "").toLowerCase();
        return role === "operations" || role === "sales"
            || (Array.isArray(user.permissions) && user.permissions.includes("manage_projects"));
    }, [user]);
    const [stages, setStages] = useState([]);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    useEffect(() => {
        (async () => {
            try {
                const r = await api.listSalesLifecycleStages().catch(() => ({ stages: [] }));
                setStages(r?.stages || []);
            } catch { /* non-fatal */ }
        })();
    }, []);
    if (!canEdit) return null;
    const current = project?.sales_stage || "";
    const change = async (next) => {
        setSaving(true); setErr("");
        try {
            const updated = await api.updateSalesStage(project.id, next || null);
            onProjectUpdated?.(updated);
            toast.success("Sales stage updated");
        } catch (e) {
            setErr(formatApiError(e?.response?.data?.detail) || e.message);
        } finally { setSaving(false); }
    };
    return (
        <section className="rounded-xl border border-[#E5E7EB] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5" data-testid="ops-sales-stage">
            <div className="flex items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                    <span aria-hidden className="inline-flex w-7 h-7 items-center justify-center rounded-lg bg-[#EEF2FF] text-[#1E3A8A] shrink-0">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20V10M6 20V4M18 20v-6"/></svg>
                    </span>
                    <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#4B5565]">
                        Sales lifecycle
                    </div>
                </div>
                <div className="text-[10px] text-[#4B5565]">Commercial stage — distinct from delivery</div>
            </div>
            <div className="flex items-center gap-2">
                <select
                    data-testid="ops-sales-stage-select"
                    value={current}
                    disabled={saving}
                    onChange={(e) => change(e.target.value)}
                    className="w-full h-10 rounded-md border border-[#E5E7EB] bg-white px-3 outline-none focus:border-[#3E9E2E]"
                >
                    <option value="">Unassigned</option>
                    {(stages.length ? stages : Object.entries(FALLBACK).map(([key, label]) => ({ key, label })))
                        .map((s) => (
                            <option key={s.key} value={s.key}
                                data-testid={`ops-sales-stage-opt-${s.key}`}>
                                {s.label}
                            </option>
                        ))}
                </select>
            </div>
            {!!err && (<div className="mt-2 text-xs text-[#DA3535]" data-testid="ops-sales-stage-error">{err}</div>)}
        </section>
    );
}
